import { memo, useEffect, useState } from 'react'
import { TouchableOpacity } from 'react-native'
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
  WithTimingConfig,
} from 'react-native-reanimated'
import { StyleSheet, useUnistyles } from 'react-native-unistyles'

import UniIcon from '@/components/unistyles/UniIcon'
import UniText from '@/components/unistyles/UniText'

interface Props {
  basicFilters: { isActive: boolean; isDirty: boolean }
  advancedFilters: { isActive: boolean; isDirty: boolean }
  onReset: () => void
}

const SPRING_CONFIG = {
  mass: 1,
  damping: 20,
  stiffness: 150,
  overshootClamping: false,
  restDisplacementThreshold: 0.01,
  restSpeedThreshold: 0.01,
}

const TIMING_CONFIG: WithTimingConfig = {
  duration: 300,
  easing: Easing.inOut(Easing.ease),
}

const AnimatedResetFiltersBanner = memo(
  ({ basicFilters, advancedFilters, onReset }: Props) => {
    const { theme } = useUnistyles()

    const height = useSharedValue(0)
    const translateY = useSharedValue(-48)
    const opacity = useSharedValue(0)
    const [isMounted, setIsMounted] = useState(false)

    const hasActiveFilters =
      (basicFilters.isActive && basicFilters.isDirty) ||
      advancedFilters.isActive

    useEffect(() => {
      if (hasActiveFilters && !isMounted) {
        setIsMounted(true)
      }

      if (hasActiveFilters) {
        // Animate everything together when showing
        height.value = withSpring(48, SPRING_CONFIG)
        translateY.value = withSpring(0, SPRING_CONFIG)
        opacity.value = withTiming(1, TIMING_CONFIG)
      } else if (isMounted) {
        // Animate everything together when hiding
        height.value = withSpring(0, SPRING_CONFIG)
        translateY.value = withSpring(-48, SPRING_CONFIG)
        opacity.value = withTiming(0, TIMING_CONFIG, (finished) => {
          if (finished) {
            runOnJS(setIsMounted)(false)
          }
        })
      }
    }, [hasActiveFilters, height, translateY, opacity, isMounted])

    const animatedStyle = useAnimatedStyle(() => ({
      height: height.value,
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    }))

    if (!isMounted) return null

    return (
      <Animated.View style={[styles.container, animatedStyle]}>
        <TouchableOpacity
          accessibilityRole="button"
          onPress={onReset}
          style={styles.button}
        >
          <UniIcon
            color={theme.colors.primary}
            size={20}
            source="filter-remove"
          />
          <UniText style={styles.text}>Reset Filters</UniText>
        </TouchableOpacity>
      </Animated.View>
    )
  }
)

const styles = StyleSheet.create((theme) => ({
  button: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[2],
    paddingHorizontal: theme.tokens.spacing[4],
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors.secondaryContainer,
    justifyContent: 'center',
    overflow: 'hidden',
    width: '100%',
  },
  text: {
    color: theme.colors.primary,
    fontSize: 16,
    fontWeight: '500',
  },
}))

AnimatedResetFiltersBanner.displayName = 'AnimatedResetFiltersBanner'

export default AnimatedResetFiltersBanner
